// Mock data for the AI Solutions landing page

export const mockAgents = [
  {
    number: 1,
    name: "Lead Generation & Prospecting",
    icon: "Target",
    triggers: "Market research triggers, competitor analysis, ideal customer profiling",
    actions: "Identifies prospects, researches companies, generates qualified lead lists, initiates outreach campaigns",
    personality: "I'll find your ideal customers before they find your competitors",
    features: ["Prospect Research", "Lead Lists", "Outreach Campaigns"],
    accentColor: "accent-orange"
  },
  {
    number: 2,
    name: "Lead Qualification & Needs Assessment",
    icon: "Users",
    triggers: "Website inquiries, referral leads, cold outreach responses",
    actions: "Conducts technical assessment, budget qualification, timeline evaluation, routes qualified leads",
    personality: "Let me understand your specific needs and qualify the opportunity",
    features: ["Technical Assessment", "Budget Qualification", "Lead Routing"],
    accentColor: "accent-purple"
  },
  {
    number: 3,
    name: "Demo & Proof-of-Concept Manager",
    icon: "PlayCircle",
    triggers: "Qualified lead with defined requirements",
    actions: "Schedules demos, creates POCs, manages technical evaluation and demonstrations",
    personality: "See it working in your environment",
    features: ["Live Demos", "POC Development", "Technical Evaluation"],
    accentColor: "accent-blue"
  },
  {
    number: 4,
    name: "Contract & Onboarding + Training Coordination",
    icon: "FileCheck",
    triggers: "Demo approval, POC success",
    actions: "Handles contracts, technical requirements gathering, team introduction, coordinates all training programs",
    personality: "Let's get your project started and your team trained",
    features: ["Contract Management", "Onboarding", "Training Coordination"],
    accentColor: "accent-green"
  },
  {
    number: 5,
    name: "Project Management & Communication",
    icon: "MessageSquare",
    triggers: "Project kickoff, milestone completions, status updates",
    actions: "Tracks progress, manages client communication, handles scope changes, oversees implementation execution",
    personality: "Here's where we are and what's next",
    features: ["Progress Tracking", "Client Communication", "Implementation"],
    accentColor: "accent-pink"
  },
  {
    number: 6,
    name: "Technical Support & Optimization",
    icon: "Settings",
    triggers: "Post-deployment, performance issues, optimization requests",
    actions: "24/7 troubleshooting, performance monitoring, enhancement suggestions, ongoing success management",
    personality: "Your system is running smoothly",
    features: ["24/7 Support", "Performance Monitoring", "Continuous Optimization"],
    accentColor: "accent-grey"
  }
];

export const mockTimeline = [
  {
    week: "W1",
    title: "Lead Qualification + Needs Assessment",
    duration: "Days 1-7",
    color: "var(--accent-purple-200)",
    description: "We start by understanding your business needs, technical requirements, and success criteria. Our first agent conducts comprehensive assessments to ensure perfect project fit.",
    activities: [
      {
        name: "Initial Consultation",
        description: "Deep-dive discovery session to understand your AI needs"
      },
      {
        name: "Technical Assessment",
        description: "Evaluate existing systems and integration requirements"
      },
      {
        name: "Budget & Timeline Planning", 
        description: "Align project scope with your budget and timeline goals"
      },
      {
        name: "Success Metrics Definition",
        description: "Define clear KPIs and success criteria for the project"
      }
    ]
  },
  {
    week: "W2",
    title: "Solution Proposal + Demo Scheduling",
    duration: "Days 8-14",
    color: "var(--accent-blue-200)",
    description: "Our solution architects design your custom AI system and create detailed proposals. We schedule demos and begin proof-of-concept development.",
    activities: [
      {
        name: "Custom Architecture Design",
        description: "Design AI solution tailored to your specific requirements"
      },
      {
        name: "Technical Proposal Creation",
        description: "Detailed project plan with timelines and deliverables"
      },
      {
        name: "Demo Scheduling & Preparation",
        description: "Set up demonstrations of similar solutions in action"
      },
      {
        name: "POC Development Start",
        description: "Begin building proof-of-concept for your use case"
      }
    ]
  },
  {
    week: "W3",
    title: "Contract + Onboarding Workflow",
    duration: "Days 15-21",
    color: "var(--accent-orange-200)",
    description: "Finalize agreements, complete technical requirements gathering, and introduce your dedicated project team. All systems prepared for implementation.",
    activities: [
      {
        name: "Contract Finalization",
        description: "Complete all legal and technical agreements"
      },
      {
        name: "Team Introduction",
        description: "Meet your dedicated AI implementation specialists"
      },
      {
        name: "Technical Requirements",
        description: "Detailed technical specifications and integration plans"
      },
      {
        name: "Implementation Setup",
        description: "Prepare all systems and environments for deployment"
      }
    ]
  },
  {
    week: "W4",
    title: "Project Management + Communication",
    duration: "Days 22-30",
    color: "var(--accent-green-200)",
    description: "Full project execution with transparent tracking, regular communication, and milestone completion. Your AI solution goes live with ongoing support.",
    activities: [
      {
        name: "AI System Deployment",
        description: "Deploy and configure your custom AI solution"
      },
      {
        name: "Integration & Testing",
        description: "Connect with existing systems and perform comprehensive testing"
      },
      {
        name: "Training & Documentation",
        description: "Team training and complete system documentation"
      },
      {
        name: "Go-Live + Support Setup",
        description: "Launch your AI system with 24/7 monitoring and support"
      }
    ]
  }
];

export const mockGuarantees = [
  {
    title: "Lead Conversion Guarantee",
    icon: "Target",
    promise: "If your first 3 qualified leads don't result in 1 signed contract, full refund",
    description: "We're confident our AI system will convert qualified leads into paying customers. If it doesn't perform as promised within your first 3 qualified opportunities, we'll provide a complete refund.",
    coverage: "First 90 days",
    terms: "Full Refund",
    accentColor: "var(--accent-purple-200)",
    textColor: "var(--accent-purple-400)"
  },
  {
    title: "Delivery Timeline Guarantee",
    icon: "Clock",
    promise: "We'll deliver your first working AI solution within 60 days or it's free",
    description: "Our 30-day implementation process is backed by a 60-day maximum delivery guarantee. If we can't get your AI system operational within 60 days, the entire project is free.",
    coverage: "Implementation phase",
    terms: "Project Free",
    accentColor: "var(--accent-blue-200)",
    textColor: "var(--accent-blue-400)"
  },
  {
    title: "Performance Guarantee",
    icon: "TrendingUp",
    promise: "90-day performance guarantee with ongoing optimization",
    description: "Your AI system will meet or exceed the performance metrics we define together. If it doesn't perform as specified, we'll optimize it at no charge until it does.",
    coverage: "90 days post-launch",
    terms: "Free Optimization",
    accentColor: "var(--accent-green-200)",
    textColor: "var(--text-primary)"
  },
  {
    title: "Satisfaction Guarantee",
    icon: "Shield",
    promise: "100% satisfaction guarantee or we'll make it right",
    description: "If you're not completely satisfied with our AI solution or service quality, we'll work with you to resolve any issues or provide appropriate compensation.",
    coverage: "Entire engagement",
    terms: "Resolution Assured",
    accentColor: "var(--accent-orange-200)",
    textColor: "var(--accent-orange-400)"
  }
];

export const mockContactInfo = {
  phone: "+1 (555) 123-4567",
  email: "contact@aithatwows.com",
  address: "San Francisco, CA",
  responseTime: "4 hours during business hours"
};

export const mockFormOptions = {
  projectTypes: [
    { value: "automation", label: "Process Automation" },
    { value: "analysis", label: "Data Analysis & Insights" },
    { value: "customer", label: "Customer Service AI" },
    { value: "sales", label: "Sales & Marketing AI" },
    { value: "custom", label: "Custom Solution" }
  ],
  budgetRanges: [
    { value: "10k-25k", label: "$10k - $25k" },
    { value: "25k-50k", label: "$25k - $50k" },
    { value: "50k-100k", label: "$50k - $100k" },
    { value: "100k+", label: "$100k+" }
  ],
  timelines: [
    { value: "immediate", label: "Immediate (1-2 weeks)" },
    { value: "month", label: "Within a month" },
    { value: "quarter", label: "This quarter" },
    { value: "exploring", label: "Just exploring" }
  ]
};